<script setup lang="ts">
import type { ComputedRef } from 'vue';
import { computed, watch } from 'vue';
import type { Emits, LocalOptions, Option, Props } from './select';

const props = withDefaults(defineProps<Props>(), {
	size: 's',
	styling: '',
});

const model = defineModel<Props['modelValue']>({
	required: true,
});

defineEmits<Emits>();

/**
 * Словарь с учетом вложенных Props['options']
 *
 * Этот словарь должен использовать в логике компонента, чтобы она не зависила от типа входных данных
 */
const optionByValue: ComputedRef<LocalOptions> = computed(() => {
	/**
	 * Преобразование:
	 * - Array => Map
	 * - string => Option
	 */
	const res = new Map();

	props.options.forEach((option, index) => {
		// базовый Options со строками
		if (typeof option === 'string') option = genOption(index, option);

		if (option.children) {
			// вложенные элементы Option для <optgroup>
			const children = new Map();

			option.children.forEach((subOption, subIndex) => {
				// базовый Options со строками
				if (typeof subOption === 'string') subOption = genOption(subIndex, subOption);

				children.set(subOption.value, subOption);
			});

			option = { ...option };
			option.children = children;

			res.set(option.value, option);
		} else {
			// самостоятельный элемент Option
			res.set(option.value, option);
		}
	});

	return res;
});

/**
 * Словарь со всеми не disabled значениями Props['options'] в порядке следования с учетом вложенных Props['options']
 */
const flatOptionByValue = computed(() => {
	const res = new Map<Props['modelValue'], Option>();

	optionByValue.value.forEach(option => {
		if (option.disabled) {
			return;
		}

		// вложенные элементы Option для <optgroup>
		if (option.children) {
			option.children.forEach(subOption => {
				if (subOption.disabled) {
					return;
				}

				res.set(subOption.value, subOption);
			});

			return;
		}

		res.set(option.value, option);
	});

	return res;
});

/**
 * Генерация Объекта option из строки
 *
 * Для базового способа указания props
 */
const genOption = (value: Props['modelValue'], title: string): Option => {
	return {
		value,
		title,
	};
};

/**
 * Логика для обртаной своместимости:
 * - null - заменить на первое доступное значение
 * - undefined - заменить на первое доступное значение
 */
watch([
	() => props.modelValue,
	flatOptionByValue,
], () => {
	if ((model.value === null || model.value === undefined) && flatOptionByValue.value.size) {
		console.warn('Пожалуйста, не передавайте в компонент Select значения null и undefined');

		model.value = flatOptionByValue.value.keys().next().value!;
	}
}, { immediate: true });

/**
 * Иконка выбранного option
 */
const optionIcon = computed(() => {
	return flatOptionByValue.value.get(model.value)?.icon;
});

/**
 * Выбрать следующее доступное значение
 */
const selectNextValue = () => {
	const listEnabledValues = [...flatOptionByValue.value.keys()];
	const currentIndex = listEnabledValues.indexOf(model.value);
	const nextIndex = (currentIndex + 1) % listEnabledValues.length;

	model.value = listEnabledValues[nextIndex];
};
</script>

<template>
	<label
		:class="{
			['top-select']:true,
			['top-select-' + name]:name,
			['top-size_' + size]: true,
			['top-formsCaptionWrapper']: !!title,
			['top-select-error']: isError,
			['top-disabled']: disabled,
		}"
		:data-value="model"
		:data-top-icon="icon"
		:data-top-icon2="optionIcon"
	>
		<select
			v-top-focus.onupdate="isError"
			:class="{
				['top-forms-focusable']: !disabled,
				['top-select_select']: true,
				['top-select_arrow']: true,
				['top-error']: isError,
				[`top-style_${styling}`]: !!styling,
			}"
			:name="name"
			:disabled="disabled"
			v-model="model"
		>
			<template v-for="[value, option] of optionByValue">
				<optgroup
					v-if="option.children"
					:key="'group_' + value"
					:label="option.title"
					:disabled="option.disabled"
				>
					<option
						v-for="[subValue, subOption] of option.children"
						:key="subValue"
						:value="subValue"
						:selected="subValue === model"
						:disabled="subOption.disabled"
					>
						{{ subOption.title }}
					</option>
				</optgroup>

				<option
					v-else
					:key="value ?? undefined"
					:value="value"
					:selected="value === model"
					:disabled="option.disabled"
				>
					{{ option.title }}
				</option>
			</template>
		</select>

		<span
			v-if="addChanger && flatOptionByValue.size && !disabled"
			class="top-changer"
			@click="selectNextValue"
		></span>

		<span
			v-if="title"
			class="top-formsCaption"
		>
			{{ title }}
		</span>
	</label>
</template>

<style>
.top-select {
	color: var(--color-text-1);
	--top-icon-color: var(--color-text-2);
	--top-icon2-color: var(--color-text-2);

	align-items: center;
	width: 180px;
	max-width: 100%;
	display: inline-flex;
}

.top-select-error {
	--top-icon-color: var(--color-text-negative);
	--top-icon2-color: var(--color-text-negative);
}

.top-select_select {
	background-color: var(--top-forms-background-color);
	/* для select вместо padding нужно использовать text-indent */
	padding-left: var(--top-forms-padding);

	/* если есть значки, добавить отступ на их ширину с учетом уже добавленного padding */
	text-indent: calc(clamp(0px, calc(var(--top-icon-both-width) - var(--top-forms-padding)), calc(var(--top-icon-both-width) - var(--top-forms-padding))));

	width: 100%;
	height: var(--top-forms-base-height);
	text-overflow: ellipsis;
	white-space: nowrap;
	flex-grow: 1;
	overflow: hidden;
}

.top-select optgroup {
	color: var(--color-text-1);
	font-style: normal;
}

.top-select option:not(:disabled) {
	background: var(--color-layout-front-1);
	color: var(--color-text-1);
}

.top-select option:disabled {
	background: unset;
	color: unset;
}

/* хром не мерняет цвет для optgroup option, стили оставлены по умолчанию */
/*
.top-select optgroup:disabled,
.top-select optgroup option,
.top-select option:disabled {

}
*/

/* https://bugzilla.mozilla.org/show_bug.cgi?id=1743066 */
@-moz-document url-prefix() {
	.top-select_select {
		text-indent: calc(clamp(0px, calc(var(--top-icon-both-width) - var(--top-forms-padding)), calc(var(--top-icon-both-width) - var(--top-forms-padding))) / 2);
	}
}

@media only screen and (min-width: 900px) {
	/* changer */
	.top-changer {
		cursor: pointer;
		border-radius: 100%;
		width: 0;
		transform: translateX(-44px);
		color: var(--color-text-2);
		font-size: 16px;
		line-height: 1;
		display: none;
		z-index: 2;
		order: 4;
	}

	.top-changer:hover {
		color: var(--color-text-primary);
	}

	.top-changer:after {
		content: "";
		background-color: var(--top-forms-background-color);
		font-family: "Topvisor-2";
	}

	*:hover > .top-changer {
		display: block;
	}
}
</style>
